<script setup>
  import { defineProps, defineEmits, ref } from 'vue'
  import { useDialogStore } from '@/stores/dialogStore'
  import draggable from 'vuedraggable'
  import EditDialog from '@/components/EditDialog.vue'

  // ダイアログストアを初期化
  const dialogStore = useDialogStore()

  // 一意のIDを生成
  function generateId() {
    return '_' + Math.random().toString(36).substr(2, 9)
  }

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    isOption: {
      type: Boolean,
      default: false,
    },
    depth: {
      type: Number,
      default: 1,
    },
  })

  const emit = defineEmits(['delete'])

  // 選択肢を追加
  function addSelection() {
    if (!props.item.selection) {
      props.item.selection = []
    }
    props.item.selection.push({
      id: generateId(), // 一意のIDを追加
      option: '',
      collapsed: false,
    })
  }

  // 重要: 削除処理を統合した新しい関数 - ダイアログストアを使用
  async function handleDelete() {
    const type = props.isOption ? '選択肢' : '項目'
    const hasChildren = props.isOption && props.item.children && props.item.children.length > 0
    const hasSelection = props.item.selection && props.item.selection.length > 0

    let message = `この${type}を削除してもよろしいですか？`

    // 子項目または選択肢がある場合は追加情報を表示
    if (hasChildren && hasSelection) {
      message += '\n\n子項目と選択肢も全て削除されます。'
    } else if (hasChildren) {
      message += '\n\n子項目も全て削除されます。'
    } else if (hasSelection) {
      message += '\n\n選択肢も全て削除されます。'
    }

    const confirmed = await dialogStore.showConfirmDialog('削除確認', message)
    if (confirmed) {
      // 確認済みフラグを付与することで二重確認を防止
      emit('delete', { confirmed: true })
    }
  }

  // 選択肢を削除 - ダイアログストア使用版
  async function deleteOption(index, event) {
    // すでに確認済みの場合は二度目の確認をスキップ
    if (event && event.confirmed) {
      props.item.selection.splice(index, 1)
      if (props.item.selection.length === 0) {
        delete props.item.selection
      }
      return
    }

    // ダイアログストアを使用して確認
    const option = props.item.selection[index]
    let message = 'この選択肢を削除してもよろしいですか？'

    // 子項目がある場合は追加情報を表示
    if (option.children && option.children.length > 0) {
      message += '\n\n子要素も全て削除されます。'
    }

    const confirmed = await dialogStore.showConfirmDialog('選択肢削除確認', message)
    if (confirmed) {
      props.item.selection.splice(index, 1)
      if (props.item.selection.length === 0) {
        delete props.item.selection
      }
    }
  }

  // 子項目を削除 - ダイアログストア使用版
  async function deleteChild(index, event) {
    // すでに確認済みの場合は二度目の確認をスキップ
    if (event && event.confirmed) {
      props.item.children.splice(index, 1)
      if (props.item.children.length === 0) {
        delete props.item.children
      }
      return
    }

    // ダイアログストアを使用して確認
    const child = props.item.children[index]
    let message = 'この子項目を削除してもよろしいですか？'

    // 選択肢がある場合は追加情報を表示
    if (child.selection && child.selection.length > 0) {
      message += '\n\n選択肢も全て削除されます。'
    }

    const confirmed = await dialogStore.showConfirmDialog('子項目削除確認', message)
    if (confirmed) {
      props.item.children.splice(index, 1)
      if (props.item.children.length === 0) {
        delete props.item.children
      }
    }
  }

  // 子項目を追加
  function addChild() {
    if (!props.item.children) {
      props.item.children = []
    }
    props.item.children.push({
      id: generateId(), // 一意のIDを追加
      label: '',
      tip: '',
      collapsed: false,
    })
  }

  // ドラッグ中の項目のスタイルをカスタマイズ
  const dragOptions = {
    animation: 150,
    ghostClass: 'ghost',
    chosenClass: 'chosen',
    dragClass: 'drag',
  }

  // ポップアップに関する状態変数
  const showPopupEdit = ref(false)
  const tipContent = ref('') // tip編集用の変数

  // ポップアップを表示する関数
  const showEditPopup = item => {
    // itemのtip内容をtipContent変数にセット
    tipContent.value = item.tip || ''
    showPopupEdit.value = true
  }

  // 保存処理
  const onSave = () => {
    // 修正: props.itemのtipを更新
    props.item.tip = tipContent.value
    showPopupEdit.value = false
  }

  // 削除処理
  const onDelete = () => {
    // props.itemのtipをクリア
    props.item.tip = ''
    tipContent.value = ''
    showPopupEdit.value = false
  }
</script>

<template>
  <div class="block" :class="{ collapsed: item.collapsed, 'is-option': isOption }">
    <div class="action-buttons">
      <!-- 選択肢追加ボタン -->
      <!-- <button v-if="!isOption" class="action-btn btn-enabled" @click="addSelection" title="選択肢を追加">
        <span class="material-symbols-outlined">flowchart</span>
      </button> -->

      <!-- 選択肢の場合のみ子項目を追加できる -->
      <!-- <button v-if="isOption" class="action-btn btn-orange" @click="addChild" title="子項目を追加">
        <span class="material-symbols-outlined">checklist</span>
      </button> -->

      <!-- ヒントボタン -->
      <button class="action-btn" :class="{ 'btn-enabled': item.tip }" @click="showEditPopup(item)" title="ヒントを表示">
        <span class="material-symbols-outlined">error</span>
      </button>

      <!-- 削除ボタン - 修正 -->
      <button class="action-btn btn-red" @click="handleDelete" title="削除">
        <span class="material-symbols-outlined">delete</span>
      </button>
    </div>

    <div class="block-header">
      <!-- ドラッグハンドル -->
      <button class="action-btn drag-handle" title="ドラッグして移動">
        <span class="material-symbols-outlined">drag_indicator</span>
      </button>
      <button class="toggle-btn" @click="item.collapsed = !item.collapsed">
        <span class="material-symbols-outlined">
          {{ item.collapsed ? 'chevron_right' : 'expand_more' }}
        </span>
      </button>
      <input v-if="isOption" v-model="item.option" placeholder="選択肢名を入力..." />
      <input v-else v-model="item.label" placeholder="項目名を入力..." />
    </div>

    <div class="content">
      <!-- 項目の場合のみtipを表示 -->
      <!-- <template v-if="!isOption">
        <input v-model="item.tip" placeholder="ヒントを入力..." />
      </template> -->

      <!-- 選択肢セクション (ドラッグ可能) -->
      <div v-if="item.selection && item.selection.length > 0">
        <!-- <div class="section-title">選択肢:</div> -->
        <div class="btn-group">
          <button class="btn btn-sm btn-border" @click="addSelection">選択肢を追加</button>
        </div>
        <div class="selection-indent">
          <draggable v-model="item.selection" item-key="id" v-bind="dragOptions" handle=".drag-handle" class="drag-container">
            <template #item="{ element, index }">
              <TodoItemBlock :item="element" :is-option="true" :depth="depth + 1" @delete="event => deleteOption(index, event)" />
            </template>
          </draggable>
        </div>
      </div>

      <!-- 子項目セクション (選択肢の場合のみ) (ドラッグ可能) -->
      <div v-if="isOption && item.children && item.children.length > 0">
        <div class="section-title">項目:</div>
        <div class="indent">
          <draggable v-model="item.children" item-key="id" v-bind="dragOptions" handle=".drag-handle" class="drag-container">
            <template #item="{ element, index }">
              <TodoItemBlock :item="element" :is-option="false" :depth="depth + 1" @delete="event => deleteChild(index, event)" />
            </template>
          </draggable>
        </div>
      </div>
      <div v-if="!isOption && (!item.selection || item.selection.length === 0)">
        <div class="btn-group">
          <button class="btn btn-sm btn-border" @click="addSelection">選択肢を追加</button>
        </div>
      </div>
      <div class="btn-group" v-if="isOption">
        <button class="btn btn-sm btn-enabled" @click="addChild">項目を追加</button>
      </div>
    </div>
    <EditDialog v-model:visible="showPopupEdit" :show-copy-button="false" save-button-text="保存" @save="onSave" @delete="onDelete">
      <template v-slot:title>
        {{ item.label || item.option || '項目' }}
      </template>
      <template v-slot:content>
        <div class="dialog-edit">
          <dl>
            <dt>ヒント</dt>
            <dd>
              <input type="text" v-model="tipContent" />
            </dd>
          </dl>
        </div>
      </template>
    </EditDialog>
  </div>
</template>

<style scoped>
  .block {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 10px;
    position: relative;
    background-color: #fff;
  }

  .block.is-option {
    background-color: #e0f0e8;
  }

  .block-header {
    display: flex;
    align-items: center;
    padding-right: 80px;
    /* アクションボタン分のスペースを確保 */
  }

  .toggle-btn {
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 3px;
    margin-right: 5px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .toggle-btn:hover {
    color: #333;
  }

  input,
  textarea {
    width: 100%;
    padding: 6px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
    background-color: #fafafa;
    font-size: 14px;
    padding: 8px 12px;
  }

  .btn {
    color: #fff;
    border: none;
    border-radius: 11px;
    font-size: 12px;
    font-weight: bold;
    min-width: 68px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s;
    white-space: nowrap;
    padding: 0px 8px;
  }

  .btn-sm {
    padding: 4px 8px;
    font-size: 12px;
  }

  .btn-blue {
    background-color: #2196f3;
  }

  .btn-enabled {
    background-color: var(--enabled-color);
  }

  .btn-orange {
    background-color: #ff9800;
  }

  .btn-red {
    background-color: #f44336;
  }

  .btn-border {
    color: var(--active-color);
    border: 1px solid var(--active-color);
  }

  .btn-group {
    display: flex;
    gap: 5px;
    margin-top: 6px;
    margin-bottom: 10px;
  }

  .action-buttons {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    gap: 5px;
    z-index: 10;
  }

  .action-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 3px;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-btn.btn-enabled {
    color: #1fbf72;
  }

  .action-btn.btn-orange {
    color: #ff9800;
  }

  .action-btn.btn-red {
    color: #f44336;
  }

  .action-btn:hover.btn-enabled {
    background-color: rgba(31, 191, 114, 0.1);
  }

  .action-btn:hover.btn-orange {
    background-color: rgba(255, 152, 0, 0.1);
  }

  .action-btn:hover.btn-red {
    background-color: rgba(244, 67, 54, 0.1);
  }

  .drag-handle {
    color: #666;
    cursor: move;
  }

  .drag-handle:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .indent {
    /* border-left: 2px solid #FF9800;
  padding-left: 8px; */
  }

  .selection-indent {
    /* border-left: 2px dashed #9C27B0;
  padding-left: 8px; */
  }

  .content {
    margin-top: 6px;
    padding-left: 29px;
  }

  .collapsed .content {
    display: none;
  }

  .section-title {
    font-size: 15px;
    font-weight: bold;
    margin-top: 12px;
    margin-bottom: 8px;
    color: #333;
    padding-bottom: 4px;
    border-bottom: 1px solid #eee;
  }

  .material-symbols-outlined {
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ドラッグ&ドロップのスタイル */
  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
    border: 1px dashed #2196f3;
  }

  .chosen {
    background-color: #f0f8ff;
  }

  .drag {
    background-color: #f5f5f5;
  }
</style>
